import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { usePartnerRoles } from '../hooks/usePartnerRoles';
import { LoadingSpinner } from '../components/feedback';
import PrivateRoute from './PrivateRoute';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard that requires both authentication AND the "Admin" role.
 *
 * - Unauthenticated users are redirected to login (via PrivateRoute).
 * - Authenticated non-admin users are redirected to /403.
 * - While the role is being resolved from the access token, a spinner is shown
 *   to avoid flashing the page or the 403 redirect.
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth0();
  const { isAdmin, isLoading: areRolesLoading } = usePartnerRoles();

  return (
    <PrivateRoute>
      {(() => {
        // Still resolving the user's roles from the access token.
        if (isAuthLoading || (isAuthenticated && areRolesLoading)) {
          return (
            <div className="flex justify-center items-center min-h-[60vh]">
              <LoadingSpinner size="lg" label="Checking permissions..." />
            </div>
          );
        }

        if (!isAdmin) {
          return <Navigate to="/403" replace />;
        }

        return <>{children}</>;
      })()}
    </PrivateRoute>
  );
};

export default AdminRoute;
